import type { RefObject } from "react";

import { Link } from "@tiptap/extension-link";
import { Mention } from "@tiptap/extension-mention";
import { Underline as UnderlineExtension } from "@tiptap/extension-underline";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  GripHorizontal,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  RotateCcw,
  Strikethrough,
  Underline,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import tippy, { type Instance } from "tippy.js";

import { MenuBar } from "@/components/ui/TiptapEditor/MenuBar.tsx";
import { usePersistedChatHeight } from "@/features/request/edit/hooks/usePersistedChatHeight";

import "./chat-textarea.css";

interface ChatTextareaProps {
  canResize?: boolean;
  chatPanelRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  disabled?: boolean;
  mentionSuggestions?: Array<{ id: string; label: string }>;
  onChange: (value: string) => void;
  onSend?: () => void;
  placeholder?: string;
  showBubbleMenu?: boolean;
  showMenuBar?: boolean;
  value: string;
}

const EMPTY_MENTION_SUGGESTIONS: Array<{ id: string; label: string }> = [];

const MIN_HEIGHT_PX = 104;
const HEIGHT_CAP_RATIO = 0.8;

export function ChatTextarea({
  canResize = false,
  chatPanelRef,
  className,
  disabled = false,
  mentionSuggestions = EMPTY_MENTION_SUGGESTIONS,
  onChange,
  onSend,
  placeholder = "Send a message...",
  showBubbleMenu = true,
  showMenuBar = false,
  value,
}: ChatTextareaProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [customHeight, setCustomHeight] = usePersistedChatHeight();
  const isDragging = useRef(false);

  const editor = useEditor(
    {
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: {
          class:
            "prose max-w-full w-full min-h-[104px] max-h-[200px] outline-none p-3 overflow-auto",
          placeholder,
        },
      },
      extensions: [
        StarterKit.configure({ code: false, codeBlock: false }),
        UnderlineExtension,
        Link.configure({
          HTMLAttributes: {
            class: "text-primary underline",
          },
          openOnClick: false,
        }),
        Mention.configure({
          HTMLAttributes: {
            class: "mention",
          },
          suggestion: {
            allowSpaces: true,
            char: "@",
            items: ({ query }) =>
              mentionSuggestions
                .filter((item) =>
                  item.label.toLowerCase().includes(query.toLowerCase()),
                )
                .slice(0, 10),
            render: () => {
              let popup: Instance | null = null;

              return {
                onExit: () => {
                  if (popup) popup.destroy();
                  popup = null;
                },

                onKeyDown: (props) => {
                  if (!popup) return false;

                  if (props.event.key === "Escape") {
                    popup.hide();

                    return true;
                  }

                  if (props.event.key === "Enter") {
                    const activeItem = popup.popper.querySelector(
                      ".mention-suggestion-item.bg-bg-secondary",
                    );

                    if (activeItem) {
                      (activeItem as HTMLElement).click();

                      return true;
                    }
                    const firstItem = popup.popper.querySelector(
                      ".mention-suggestion-item",
                    );

                    if (firstItem) {
                      (firstItem as HTMLElement).click();

                      return true;
                    }
                  }

                  return false;
                },

                onStart: (props) => {
                  const { clientRect } = props;

                  if (!clientRect) return;

                  popup = tippy(document.createElement("div"), {
                    appendTo: () => document.body,
                    arrow: true,
                    content: () => {
                      const container = document.createElement("div");

                      container.className = "mention-suggestions";

                      const component = document.createElement("div");

                      container.append(component);

                      const reactElement = document.createElement("div");

                      component.append(reactElement);

                      setTimeout(() => {
                        const root = document.createElement("div");

                        reactElement.append(root);

                        // Manually render mention list
                        const mentionList = document.createElement("div");

                        mentionList.className = "mention-list";

                        for (const item of props.items) {
                          const button = document.createElement("button");

                          button.className = "mention-suggestion-item";
                          button.textContent = `@${item.label}`;
                          button.addEventListener("click", () => {
                            props.command(item);
                            if (popup) popup.hide();
                          });
                          mentionList.append(button);
                        }

                        if (props.items.length === 0) {
                          const noResults = document.createElement("div");

                          noResults.className = "mention-no-results";
                          noResults.textContent = "No results";
                          mentionList.append(noResults);
                        }

                        root.append(mentionList);
                      }, 0);

                      return container;
                    },
                    getReferenceClientRect: () => {
                      if (!clientRect) {
                        return {
                          bottom: 0,
                          height: 0,
                          left: 0,
                          right: 0,
                          top: 0,
                          width: 0,
                        } as DOMRect;
                      }

                      const rect = clientRect();

                      return (
                        rect ||
                        ({
                          bottom: 0,
                          height: 0,
                          left: 0,
                          right: 0,
                          top: 0,
                          width: 0,
                        } as DOMRect)
                      );
                    },
                    interactive: true,
                    onCreate: (instance) => {
                      instance.popper.dataset.reactAriaTopLayer = "";
                    },
                    placement: "bottom-start",
                    showOnCreate: true,
                    trigger: "manual",
                  });

                  return popup;
                },

                onUpdate: (props) => {
                  if (!popup) return;

                  const { clientRect } = props;

                  popup.setProps({
                    getReferenceClientRect: () => {
                      if (!clientRect) {
                        return {
                          bottom: 0,
                          height: 0,
                          left: 0,
                          right: 0,
                          top: 0,
                          width: 0,
                        } as DOMRect;
                      }

                      const rect = clientRect();

                      return (
                        rect ||
                        ({
                          bottom: 0,
                          height: 0,
                          left: 0,
                          right: 0,
                          top: 0,
                          width: 0,
                        } as DOMRect)
                      );
                    },
                  });

                  // Clear existing content and create new one
                  const container = popup.popper.querySelector(
                    ".mention-suggestions",
                  );

                  if (container) {
                    container.innerHTML = "";

                    const mentionList = document.createElement("div");

                    mentionList.className = "mention-list";

                    for (const item of props.items) {
                      const button = document.createElement("button");

                      button.className = "mention-suggestion-item";
                      button.textContent = `@${item.label}`;
                      button.addEventListener("click", () => {
                        props.command(item);
                        if (popup) popup.hide();
                      });
                      mentionList.append(button);
                    }

                    if (props.items.length === 0) {
                      const noResults = document.createElement("div");

                      noResults.className = "mention-no-results";
                      noResults.textContent = "No results";
                      mentionList.append(noResults);
                    }

                    container.append(mentionList);
                  }
                },
              };
            },
          },
        }),
      ],
      onUpdate: ({ editor }) => {
        const json = editor.getJSON();

        // Check if content is actually empty (workaround for TipTap empty content detection)
        if (
          !json.content ||
          json.content.length === 0 ||
          (json.content[0] && !("content" in json.content[0]))
        ) {
          return;
        }

        // Check if the first content node is empty
        const firstContent = json.content[0];

        if (
          firstContent &&
          firstContent.type === "paragraph" &&
          (!firstContent.content || firstContent.content.length === 0)
        ) {
          onChange("");

          return;
        }

        onChange(editor.getHTML());
      },
    },
    [mentionSuggestions],
  );

  // Handle Enter key to send message
  useEffect(() => {
    if (!editor || !onSend) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        onSend();
      }
    };

    editor.view.dom.addEventListener("keydown", handleKeyDown);

    return () => {
      editor.view.dom.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, onSend]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const currentContent = editor.getHTML();

    if (value !== currentContent) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const appliedHeight = canResize ? customHeight : null;

  useEffect(() => {
    if (!editor) return;

    if (appliedHeight) {
      editor.view.dom.style.height = `${appliedHeight}px`;
      editor.view.dom.style.maxHeight = `${appliedHeight}px`;
    } else {
      editor.view.dom.style.removeProperty("height");
      editor.view.dom.style.removeProperty("max-height");
    }
  }, [editor, appliedHeight]);

  useEffect(() => {
    if (!canResize || customHeight === null) return;

    const panel = chatPanelRef?.current;

    if (!panel) return;

    const reclamp = () => {
      const maxHeight = Math.max(
        MIN_HEIGHT_PX,
        panel.getBoundingClientRect().height * HEIGHT_CAP_RATIO,
      );

      setCustomHeight((current) =>
        current !== null && current > maxHeight ? maxHeight : current,
      );
    };

    const observer = new ResizeObserver(reclamp);

    observer.observe(panel);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canResize, customHeight !== null, chatPanelRef]);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!editor) return;
      e.preventDefault();
      const handle = e.currentTarget;

      handle.setPointerCapture(e.pointerId);
      isDragging.current = true;

      const startY = e.clientY;
      const startHeight = editor.view.dom.getBoundingClientRect().height;

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!isDragging.current) return;

        const panelHeight =
          chatPanelRef?.current?.getBoundingClientRect().height ?? Infinity;
        const maxHeight = Math.max(
          MIN_HEIGHT_PX,
          panelHeight * HEIGHT_CAP_RATIO,
        );
        // Handle sits at the top edge, so dragging up (negative delta) grows the box.
        const delta = startY - moveEvent.clientY;

        setCustomHeight(
          Math.min(maxHeight, Math.max(MIN_HEIGHT_PX, startHeight + delta)),
        );
      };

      const stopDragging = () => {
        isDragging.current = false;
        handle.removeEventListener("pointermove", onPointerMove);
        handle.removeEventListener("pointerup", stopDragging);
        handle.removeEventListener("pointercancel", stopDragging);
      };

      handle.addEventListener("pointermove", onPointerMove);
      handle.addEventListener("pointerup", stopDragging);
      handle.addEventListener("pointercancel", stopDragging);
    },
    [editor, chatPanelRef, setCustomHeight],
  );

  const handleResetHeight = useCallback(() => {
    setCustomHeight(null);
  }, [setCustomHeight]);

  const containerClasses = [
    "relative w-full rounded-lg transition-colors text-text-primary",
    isFocused
      ? "border-0 shadow-lg shadow-primary/40 ring-2 ring-gray-300"
      : "border border-border-primary",
    disabled ? "opacity-50 cursor-not-allowed" : "",
    className || "",
  ]
    .join(" ")
    .trim();

  return (
    <div className={containerClasses}>
      {canResize && (
        <div className="flex items-center justify-center gap-1">
          <div
            className="flex-1 flex items-center justify-center h-2 cursor-row-resize touch-none group"
            onPointerDown={onResizePointerDown}
          >
            <GripHorizontal
              className="text-fg-tertiary group-hover:text-fg-secondary transition-colors"
              size={14}
            />
          </div>
          {customHeight !== null && (
            <button
              aria-label="Reset chat box height"
              className="absolute right-1 top-1 z-10 flex h-5 w-5 cursor-pointer items-center justify-center rounded text-fg-tertiary transition-colors hover:bg-bg-secondary hover:text-fg-secondary"
              onClick={handleResetHeight}
              type="button"
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      )}
      {editor && showMenuBar && <MenuBar disabled={false} editor={editor} />}
      {editor && showBubbleMenu && (
        <BubbleMenu
          className="bubble-menu"
          editor={editor}
          tippyOptions={{ duration: 100 }}
        >
          <button
            aria-label="Bold"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("bold") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBold().run();
            }}
          >
            <Bold size={16} />
          </button>
          <button
            aria-label="Italic"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("italic") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleItalic().run();
            }}
          >
            <Italic size={16} />
          </button>
          <button
            aria-label="Underline"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("underline") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleUnderline().run();
            }}
          >
            <Underline size={16} />
          </button>
          <button
            aria-label="Strikethrough"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("strike") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleStrike().run();
            }}
          >
            <Strikethrough size={16} />
          </button>
          <button
            aria-label="Bullet List"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("bulletList") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBulletList().run();
            }}
          >
            <List size={16} />
          </button>
          <button
            aria-label="Ordered List"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("orderedList") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleOrderedList().run();
            }}
          >
            <ListOrdered size={16} />
          </button>
          <button
            aria-label="Link"
            className={`p-2 hover:bg-bg-secondary ${editor.isActive("link") ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              const url = window.prompt(
                "URL",
                editor.getAttributes("link").href || "https://",
              );

              if (url === null) return;
              if (url === "") {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .unsetLink()
                  .run();

                return;
              }
              editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: url })
                .run();
            }}
          >
            <LinkIcon size={16} />
          </button>
        </BubbleMenu>
      )}
      <EditorContent
        className="tiptap"
        editor={editor}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
      />
    </div>
  );
}
